import { push } from "react-router-redux";
import translationService from "../app/TranslationService";
import { addQueryParams } from "../utils/urlUtils";
import { locationChanged } from "./Routing";
import { constructRequestEntity } from "../utils/dataUtils";
import promiseAllSettled from "../utils/promiseAllSettled";
import webuiState from "../app/InternalStateService";
import { WEBUILogs } from "@tinqin/tinqin-utils/src/logging";

const CREATE_DASHBOARD = "CREATE_DASHBOARD";
const UPDATE_DASHBOARD = "UPDATE_DASHBOARD";
const CLEAR_WIDGETS_DATA = "CLEAR_WIDGETS_DATA";
const CLEAR_WIDGETS_CONFIG = "CLEAR_WIDGETS_CONFIG";
const CLEAR_WIDGETS_UISTATE = "CLEAR_WIDGETS_UISTATE";
const CLEAR_WIDGET_DATA = "CLEAR_WIDGET_DATA";
const CLEAR_WIDGET_CONFIG = "CLEAR_WIDGET_CONFIG";
const CLEAR_WIDGET_UISTATE = "CLEAR_WIDGET_UISTATE";
const CLEAR_WIDGET_ENTITIES_UI_VALIDATION_STATE = "CLEAR_WIDGET_ENTITIES_UI_VALIDATION_STATE";
const CLEAR_DASHBOARD_UISTATE = "CLEAR_DASHBOARD_UISTATE";

function switchDashboard(sirenAction, widgetId, currentPath) {
    return async (dispatch, getState) => {
        //switching page address only
        if (sirenAction.properties && sirenAction.properties.silent) {
            return dispatch(
                switchDashboardSilent(sirenAction, { sirenAction, widgetId, currentPath })
            );
        }
        if (sirenAction.properties && sirenAction.properties.openNewTab) {
            return dispatch(openDashboardNewTab(sirenAction));
        }
        let dashboardDescription = getDashboardDescription(sirenAction);
        let { pathname, search } = await getDashboardAddress(sirenAction);
        //same dashboard check
        let applicationBoot = getState().getIn(["global", "applicationBoot"]);
        if (!applicationBoot && webuiState.hasAction("application.compareDashboardAddresses")) {
            let sameDashboard = await webuiState.getActionByName(
                "application.compareDashboardAddresses"
            )({ pathname, search, sirenAction });
            if (sameDashboard) {
                return Promise.reject("Dashboard remains the same.");
            }
        }
        //let routes = getState().getIn(["routes", "routes"]);
        // let currentAddress;
        //get last activated route
        // if (routes) {
        //     currentAddress = routes.get(routes.size - 1);
        // }
        // //change dashboard only if newLocation is different
        // if (newAddress === currentAddress) {
        //     console.log("Dashboard remains the same.");
        //     return Promise.reject("Dashboard remains the same.");
        // }
        await dispatch(manageActiveFlow(sirenAction));
        //await dispatch(clearGlobalState());
        await dispatch(webuiState.getActionByName("modal.clearGlobalModalState")());
        //case refresh and webuiState.getGlobalProperties("dashboardPathname") === "queryParams"
        let storageDashboard = getLocalStorageDashboard({ pathname, search }, getState());
        Object.assign(dashboardDescription, storageDashboard.dashboard);
        await dispatch(
            manageActiveFlow(Object.assign({}, dashboardDescription, sirenAction.targets))
        );
        //bulk loading by default for initial siren action
        //TODO to be a part of properties
        //let bulkLoading = sirenAction.bulkLoading;
        return dispatch(
            //executeSwitchDashboardAction(dashboard, { bulkLoading, pathname: newAddress })
            executeSwitchDashboardAction(
                dashboardDescription,
                {
                    pathname: storageDashboard.pathname || pathname,
                    search: storageDashboard.search || search
                },
                { sirenAction, widgetId, currentPath }
            )
        );
    };
}

//mostly for switching the browser address
//presumes we are switching to the same dashboard (same id)
//but with different parameters and page address
//to trigger application analytics actions
//no data loading
//no flow management
//no scrolling
//will replace browser history
function switchDashboardSilent(sirenAction, actionContext) {
    return async (dispatch, getState) => {
        let dashboard = getDashboardDescription(sirenAction);
        let { pathname, search } = await getDashboardAddress(sirenAction);
        //case refresh and webuiState.getGlobalProperties("dashboardPathname") === "queryParams"
        let storageDashboard = getLocalStorageDashboard({ pathname, search }, getState());
        Object.assign(dashboard, storageDashboard.dashboard);
        WEBUILogs.log("Remaining on the same dashboard with new address: " + pathname + search);
        //when saving dashboard action for later use on Prev/Next browser actions:
        //remove "silent" flag in order to execute full switchContainer action
        if (dashboard.properties && dashboard.properties.silent) {
            dashboard.properties = Object.assign({}, dashboard.properties);
            delete dashboard.properties.silent;
        }
        //update browser location
        //without adding new entry to browser history
        dispatch(
            webuiState.getActionByName("routing.dispatchUpdateLocation")(
                {
                    pathname,
                    search,
                    state: {
                        dashboard
                    }
                },
                {
                    replace: true
                }
            )
        );
        triggerActionForAllContexts("dashboard.onLocationUpdated", [
            {
                dashboard,
                ...actionContext
            }
        ]);
    };
}

//opens new tab with the current dashboard address
function openDashboardNewTab(sirenAction) {
    // windowRef was created because Safari doesn't supports window.open in async functions
    let windowRef = window.open();
    return async () => {
        let { pathname = "", search = "" } = await getDashboardAddress(sirenAction);
        let { ROOT_PATH_FULL } = webuiState.getWebConfig();
        if (ROOT_PATH_FULL === undefined) {
            ROOT_PATH_FULL = window.location.origin;
        }
        let newPath = `${ROOT_PATH_FULL}${pathname}${search}`;
        windowRef.location.href = newPath;
    };
}

export function switchContainer(sirenAction, widget, currentPath) {
    return async (dispatch, getState) => {
        if (!sirenAction.hasOwnProperty("targets")) {
            WEBUILogs.error("Action switch container has failed - no targets provided!");
            return Promise.reject();
        }
        let { targets = [widget], href = "" } = sirenAction;
        let targetContainer = targets[0];
        let widgetId = targets[0];
        if (targetContainer === "{root}") {
            return dispatch(switchDashboard(sirenAction, widget, currentPath));
        }
        //covering cases where targets:["{root}.{widgetId}"]
        if (targetContainer.startsWith("{root}.")) {
            let targetsSplit = targetContainer.split(".");
            widgetId = targetsSplit[1];
        }
        let state = getState();
        await dispatch(manageActiveFlow(sirenAction));
        //saving widget properties before clearing data
        const parentWidget = getWidgetProperty(state, widgetId, "parentWidget");
        const context = getWidgetProperty(state, widgetId, "context");
        //clear states (data, config, ui and validations) of target and inner widgets
        let widgetsToBeCleared = getInnerWidgets(widgetId, state, [widgetId]);
        await dispatch(clearStateWidgetUIState(widgetsToBeCleared));
        await dispatch(clearStateWidgetUIValidationState(widgetsToBeCleared));
        await dispatch(clearStateData(widgetsToBeCleared));
        await dispatch(clearStateConfig(widgetsToBeCleared));
        //using widget configuration inside the dashboard for configuring the loading of the widget
        const widgetDashboardConfig = getCurrentDashboardWidgetConfig(widgetId);
        //TODO deprecate sirenAction.skipLoadingAnimation - better use properties.skipLoadingAnimation if necessary
        let skipLoadingAnimation = sirenAction.skipLoadingAnimation;
        if (skipLoadingAnimation === undefined) {
            skipLoadingAnimation = widgetDashboardConfig.skipLoadingAnimation;
        }
        let waitInnerWidgetsLoading = widgetDashboardConfig.waitInnerWidgetsLoading;
        const loadWidget = webuiState.getActionByName("widget.loadWidget");
        return dispatch(
            loadWidget(
                {
                    href: href,
                    id: widgetId,
                    type: "widgetContainer",
                    context,
                    parentWidget
                },
                skipLoadingAnimation,
                sirenAction.parameters,
                waitInnerWidgetsLoading
            )
        );
    };
}

export function manageActiveFlow(sirenAction) {
    //let targets = sirenAction.targets || [];
    let properties = sirenAction.properties || {};
    return async (dispatch) => {
        //one reducer for set/reset flow target
        if (properties.flowContext) {
            //epts case when flow is initialized with root
            // let target = targets[0];
            // if (target === "{root}") {
            //     target = "{root}.flowContent";
            // }
            // let flowState = Object.assign({}, properties.flowContext, { flowTarget: target });
            let flowState = Object.assign({}, properties.flowContext);
            await dispatch(webuiState.getActionByName("flow.triggerSetFlowContext")(flowState));
            //check how the unload message will reset flow flag
            //logic transferred to the widget
            // let switchContainerConfig = webuiState.getActionsDefaultConfig()["switchContainer"] || {};
            // if (switchContainerConfig.updateMode) {
            //     await dispatch(webuiState.getActionByName("application.setApplicationUpdateMode")(true));
            // }
            return Promise.resolve();
        }

        return dispatch(triggerExitConfirm(sirenAction));
    };
}

export function triggerExitConfirm(sirenAction) {
    return async (dispatch, getState) => {
        let targets = sirenAction.targets || [];

        let flowContext = getState().getIn(["global", "flowContext"]) || {};
        let flowActive = flowContext.active || false;
        //let flowTarget = flowContext.flowTarget || "";
        let flowTemplate = flowContext.template || "";

        let globalConfiguration = getState().getIn(["global", "globalConfig"]);
        let notificationMessage = {
            type: "info",
            message: translationService.translate("beforeExitFlowMessage"),
            title: translationService.translate("dialogTitleAttention")
        };
        if (globalConfiguration) {
            let globalFlowContext = globalConfiguration["flowContext"] || {};
            let flow = globalFlowContext.hasOwnProperty(flowTemplate);
            if (flow) {
                notificationMessage = globalFlowContext[flowTemplate]["exitMessage"];
            }
        }

        //if (flowActive && (targets.includes(flowTarget) || flowTarget.includes("{root}"))) {
        //targets.length epts quit flow
        if (flowActive && (!targets.length || targets[0] === "{root}")) {
            await dispatch(
                webuiState.getActionByName("messages.triggerConfirmMessage")(notificationMessage)
            );
            await dispatch(webuiState.getActionByName("flow.triggerSetFlowContext")());
            //check how the unload message will reset flow flag
            //logic transferred to the widget
            //await dispatch(webuiState.getActionByName("application.setApplicationUpdateMode")(false));
        }

        return Promise.resolve();
    };
}

function executeSwitchDashboardAction(dashboard, additionalParams = {}, actionContext) {
    const dispatchUpdateRoutes = webuiState.getActionByName("routing.dispatchUpdateRoutes");
    return async (dispatch, getState) => {
        WEBUILogs.log("New dashboard at " + additionalParams.pathname + additionalParams.search);
        let currentDashboard = getCurrentDashboard(getState());
        if (!currentDashboard || currentDashboard.get("id") !== dashboard.id) {
            dispatch(createDashboard(dashboard));
        }

        await dispatch(loadDashboard(dashboard, actionContext));
        //trigger action to notify client application for the change
        let historyNavigationFlag =
            actionContext.sirenAction.properties?.webuiOrigin === "historyNavigation";
        triggerActionForAllContexts("dashboard.onDashboardLoaded", [
            {
                dashboard,
                historyNavigation: historyNavigationFlag,
                ...actionContext
            }
        ]);
        //scrolling to top once the new dashboard is loaded
        if (!webuiState.getGlobalProperties("skipDefaultScrollPosition")) {
            scrollToTop();
        }
        await dispatch(dispatchUpdateRoutes(dashboard.address));
        //update browser location
        dispatch(
            webuiState.getActionByName("routing.dispatchUpdateLocation")({
                pathname: additionalParams.pathname,
                search: additionalParams.search,
                state: {
                    dashboard
                }
            })
        );
        triggerActionForAllContexts("dashboard.onLocationUpdated", [
            {
                dashboard,
                historyNavigation: historyNavigationFlag,
                ...actionContext
            }
        ]);
        const result = await dispatch(initDashboard(additionalParams));
        //trigger action to notify client application for the change
        triggerActionForAllContexts("dashboard.onWidgetsLoaded", [
            {
                dashboard,
                historyNavigation: historyNavigationFlag,
                ...actionContext
            }
        ]);
        return result;
    };
}

function triggerActionForAllContexts(actionName, parameters) {
    const dispatch = webuiState.getDispatchMethod();
    const allContexts = webuiState.getAllContextIds();
    //dispatch in global context
    if (webuiState.hasAction(actionName)) {
        dispatch(webuiState.getActionByName(actionName)(...parameters));
    }
    //dispatch for all webui contexts
    allContexts.forEach((contextId) => {
        if (webuiState.hasAction(actionName, { contextId, strict: true }))
            dispatch(
                webuiState.getActionByName(actionName, { contextId, strict: true })(...parameters)
            );
    });
}

function createDashboard(options) {
    //if missing creates new dashboard object
    return {
        type: CREATE_DASHBOARD,
        payload: options
    };
}

export function reloadCurrentDashboard(sirenAction, widgetId, currentPath) {
    //TODO DELETE
    let dashboard = getCurrentDashboard();
    if (!dashboard.id) {
        return Promise.reject("Missing current dashboard.");
    }
    return loadDashboard(dashboard, { sirenAction, widgetId, currentPath });
}

function loadDashboard(dashboard, actionContext) {
    return async (dispatch, getState) => {
        let size = getState().getIn(["windowSize", "size"]);
        let configPromise = dispatch(
            fetchDashboardConfig(dashboard.configUrl, size, dashboard.id, actionContext)
        );
        let dataPromise = dispatch(fetchDashboardData(dashboard.dataUrl, size, actionContext));
        //waiting for both data and config to load
        let [config, data] = await Promise.all([configPromise, dataPromise]);
        dispatch(updateDashboard(dashboard.id, config, data));
        if (config.theme) {
            return dispatch(webuiState.getActionByName("theme.setTheme")(config.theme));
        }
        return [config, data];
    };
}

function fetchDashboardData(
    endpoint,
    size,
    { sirenAction = {}, widgetId = "", currentPath = "" } = {}
) {
    return async (dispatch, getState) => {
        let actionResult;
        let requestPayload = {};
        if (sirenAction.method === "POST") {
            requestPayload = constructRequestEntity(
                sirenAction.sources,
                getState(),
                currentPath,
                sirenAction,
                widgetId
            );
        }
        try {
            //====== START: usage of preMadeRequests
            let preMadeRequests = webuiState.getGlobalProperties("preMadeRequests");
            if (preMadeRequests) {
                actionResult = await preMadeRequests.dashboard.data;
                //======END: usage of preMadeRequests
            } else {
                //adding screenSize
                if (endpoint.includes("?")) {
                    endpoint += `&screenSize=${size}`;
                } else {
                    endpoint += `?screenSize=${size}`;
                }
                actionResult = await dispatch(
                    webuiState.getActionByName("request.request")(
                        {
                            method: sirenAction.method || "GET",
                            //example: `${DASHBOARD_ROOT}/${dashboard}/object/${entity}/widgets/`
                            endpoint: endpoint,
                            headers: {
                                "Content-Type":
                                    sirenAction.type ||
                                    sirenAction.contentType ||
                                    "application/json"
                            },
                            payload: requestPayload
                        },
                        {
                            widgetId
                        }
                    )
                );
            }
        } catch (error) {
            actionResult = Promise.reject(error);
            dispatch(loadDashboardFailure(error));
        }
        return actionResult;
    };
}

function fetchDashboardConfig(configUrl, size, id, { widgetId }) {
    if (webuiState.getGlobalProperties("screenSizeQueryParameter")) {
        //adding screenSize
        if (configUrl.includes("?")) {
            configUrl += `&screenSize=${size}`;
        } else {
            configUrl += `?screenSize=${size}`;
        }
    }
    return async (dispatch) => {
        let actionResult;
        try {
            let options = {
                method: "GET",
                endpoint: configUrl
            };
            let { STATIC_CONFIGURATIONS_ROOT } = webuiState.getWebConfig();
            if (STATIC_CONFIGURATIONS_ROOT) {
                options.endpoint = STATIC_CONFIGURATIONS_ROOT + configUrl.split("?")[0] + ".json";
                options.rawEndpointUrl = true;
            }
            const urlParser = webuiState.getStaticConfigUrlParser({ widgetId });
            if (urlParser) {
                options.endpoint = await urlParser({
                    context: "dashboard",
                    endpoint: configUrl,
                    id
                });
                options.rawEndpointUrl = true;
            }
            //application is responsible for loading static configuration
            let staticConfigLoader = webuiState.getStaticConfigLoader({ widgetId });
            let lazyModuleLoader = webuiState.getLazyModuleLoader({ widgetId });
            if (staticConfigLoader) {
                actionResult = await staticConfigLoader.load({
                    id,
                    type: "dashboard",
                    defaultRequestOptions: options
                });
            } else if (lazyModuleLoader) {
                let module = await lazyModuleLoader.load({
                    id,
                    type: "dashboard"
                });
                actionResult = module.config;
            } else {
                //default behaviour is to make a request for UI configuration
                actionResult = await dispatch(
                    webuiState.getActionByName("request.request")(options)
                );
            }
        } catch (error) {
            actionResult = Promise.reject(error);
            dispatch(loadDashboardFailure(error));
        }
        return actionResult;
    };
}

//save new config and data for dashboard
function updateDashboard(id, config, data) {
    return {
        type: UPDATE_DASHBOARD,
        payload: {
            id,
            config,
            data
        }
    };
}

//clears activeFlow flag
//clears all widgets data/config/uiState unless cached
//dashboard config is cached by default
//(for smoother screen transitions)
//forceReset to ignore widget caching
export function clearGlobalState(forceReset) {
    return (dispatch, getState) => {
        let widgetsConfig = getState().get("config");
        let cachedWidgets = [];
        if (!forceReset) {
            cachedWidgets = widgetsConfig
                .filter((widgetConfig) => {
                    return widgetConfig.get("cached") || widgetConfig.get("static");
                })
                .toObject();
        }
        dispatch(webuiState.getActionByName("flow.triggerResetActiveFlow")());
        dispatch(clearWidgetsData(cachedWidgets));
        dispatch(clearWidgetsConfig(cachedWidgets));
        //all ui State is cleared by default
        //in case ui State should be preserved throughout different dashboards
        //another type of caching flag should be used
        //Extranet case: ItemPopovers in cached header menu
        //"cached" property - cache the widget and ui state
        //"cachedUI" - cache only the ui state, but the widget will update its data
        let cachedUiWidgets = widgetsConfig.filter((widgetConfig) => {
            return widgetConfig.get("cachedUi") || widgetConfig.get("cached");
        });
        dispatch(clearWidgetsUIState(cachedUiWidgets.toObject()));
        dispatch(clearWidgetEntitiesUIValidationState(cachedUiWidgets));
        dispatch(clearDashboardUIState());
    };
}

function clearStateData(widgets) {
    return (dispatch, getState) => {
        if (widgets.length) {
            widgets.forEach((widget) => {
                let widgetCached = getState().getIn(["config", widget, "cached"]);
                if (!widgetCached) {
                    //TODO make reducer to clear widget list
                    dispatch(clearWidgetData(widget));
                }
            });
        }
        return Promise.resolve();
    };
}
function clearWidgetData(widgetID) {
    return {
        type: CLEAR_WIDGET_DATA,
        payload: {
            widgetId: widgetID
        }
    };
}
function clearStateConfig(widgets) {
    return (dispatch, getState) => {
        if (widgets.length) {
            widgets.forEach((widget) => {
                let widgetCached = getState().getIn(["config", widget, "cached"]);
                let widgetCachedUi = getState().getIn(["config", widget, "cachedUi"]);
                if (!widgetCached && !widgetCachedUi) {
                    //TODO make reducer to clear widget list
                    dispatch(clearWidgetConfig(widget));
                }
            });
        }
        return Promise.resolve();
    };
}
function clearWidgetConfig(widgetID) {
    return {
        type: CLEAR_WIDGET_CONFIG,
        payload: {
            widgetId: widgetID
        }
    };
}

function clearStateWidgetUIState(widgets = []) {
    return (dispatch, getState) => {
        if (widgets.length) {
            widgets.forEach((widget) => {
                let widgetCachedUi = getState().getIn(["config", widget, "cachedUi"]);
                if (!widgetCachedUi) {
                    //TODO make reducer to clear widget list
                    dispatch(clearWidgetUIState(widget));
                }
            });
        }
        return Promise.resolve();
    };
}
function clearStateWidgetUIValidationState(widgets) {
    return (dispatch) => {
        return dispatch(clearWidgetEntitiesUIValidationState(widgets));
    };
}
function clearWidgetUIState(widgetID) {
    return {
        type: CLEAR_WIDGET_UISTATE,
        payload: {
            widgetId: widgetID
        }
    };
}

function clearWidgetsData(cachedWidgets) {
    return {
        type: CLEAR_WIDGETS_DATA,
        payload: {
            cachedWidgets: cachedWidgets
        }
    };
}

function clearWidgetsConfig(cachedWidgets) {
    return {
        type: CLEAR_WIDGETS_CONFIG,
        payload: {
            cachedWidgets: cachedWidgets
        }
    };
}

function clearWidgetsUIState(cachedWidgets) {
    return {
        type: CLEAR_WIDGETS_UISTATE,
        payload: {
            cachedWidgets: cachedWidgets
        }
    };
}

function clearWidgetEntitiesUIValidationState(cachedWidgets) {
    return {
        type: CLEAR_WIDGET_ENTITIES_UI_VALIDATION_STATE,
        payload: {
            widgets: cachedWidgets
        }
    };
}

function clearDashboardUIState() {
    return {
        type: CLEAR_DASHBOARD_UISTATE
    };
}

export function setDashboardUIState(uiState) {
    const setUiState = webuiState.getActionByName("component.setUiState");
    return (dispatch) => dispatch(setUiState("dashboards", "current", uiState));
}

//Scrolling page to top
export function scrollToTop() {
    //Chrome/Edge case
    document.body.scrollTop = 0;
    //Rest of the world
    document.documentElement.scrollTop = 0;
}

//reloads dashboard widgets using global loading animation, does not trigger updateRoutes action
export function reloadDashboardAndWidgets(action = {}) {
    return (dispatch) => {
        if (!action.skipLoadingAnimation) {
            dispatch(webuiState.getActionByName("loading.triggerGlobalLoading")());
            dispatch(clearGlobalState(true));
        }
        let promise = dispatch(loadAllWidgets(true));

        if (!action.skipLoadingAnimation) {
            promise.then(() => {
                return dispatch(webuiState.getActionByName("loading.dismissGlobalLoading")());
            });
        }
    };
}

export function reinitDashboard(location) {
    // const dispatchUpdateRoutes = webuiState.getActionByName(
    //     "routing.dispatchUpdateRoutes"
    // );
    //console.log("Reinit Dashboard");
    //let currentPath = location.pathname + location.search;
    let currentLocationState = location.state || {};
    return async (dispatch, getState) => {
        let flowContext = getState().getIn(["global", "flowContext"]) || {};
        let flowActive = flowContext.active || false;
        if (flowActive) {
            await dispatch(webuiState.getActionByName("flow.triggerSetFlowContext")());
        }
        if (currentLocationState.dashboard) {
            let action = {
                href: currentLocationState.dashboard.address,
                properties: Object.assign(
                    { webuiOrigin: "historyNavigation" },
                    currentLocationState.dashboard.properties
                ),
                parameters: currentLocationState.dashboard.parameters,
                targets: ["{root}"]
            };
            return dispatch(switchDashboard(action));
        } else {
            return dispatch(webuiState.getActionByName("application.loadAppBoot")());
        }
    };
}

export function switchToRoute(route) {
    return (dispatch) => {
        dispatch(push(route));
        dispatch(reinitDashboard(window.location));
    };
}

//clears global state, updates routes and loads all widgets (with or without a global loading)
function initDashboard(props = {}) {
    return (dispatch, getState) => {
        let globalLoading = props.bulkLoading;
        const applicationLoadingState = getState().getIn(["global", "applicationLoading"]);
        const waitInnerWidgetsLoading = webuiState.getGlobalProperties("waitInnerWidgetsLoading");
        const mainAreaLoadingFlag =
            webuiState.getGlobalProperties("mainAreaLoadingFlag") &&
            !applicationLoadingState &&
            !globalLoading;
        const skipLoadingAnimation = globalLoading || mainAreaLoadingFlag;
        if (globalLoading) {
            dispatch(webuiState.getActionByName("loading.triggerGlobalLoading")());
        } else if (mainAreaLoadingFlag) {
            dispatch(webuiState.getActionByName("loading.triggerMainAreaLoading")());
        }
        //clear state
        dispatch(clearGlobalState());

        //load all widgets
        return dispatch(loadAllWidgets(skipLoadingAnimation, waitInnerWidgetsLoading))
            .then((result) => {
                if (globalLoading) {
                    //wait before showing new widgets, to render more smoothly
                    setTimeout(() => {
                        dispatch(webuiState.getActionByName("loading.dismissGlobalLoading")());
                    }, 50);
                } else if (mainAreaLoadingFlag) {
                    //wait before showing new widgets, to render more smoothly
                    setTimeout(() => {
                        dispatch(webuiState.getActionByName("loading.dismissMainAreaLoading")());
                    }, 50);
                }
                return result;
            })
            .then(() => {
                webuiState.setGlobalProperties({
                    preMadeRequests: null
                });
            });
    };
}

//reloads all widgets (config/data) for the current dashboard
function loadAllWidgets(skipLoadingAnimation = false, waitInnerWidgetsLoading = false) {
    const loadWidget = webuiState.getActionByName("widget.loadWidget");
    return (dispatch, getState) => {
        let currentDashboard = getCurrentDashboard(getState());
        if (!currentDashboard) {
            return Promise.reject("Missing current dashboard.");
        }
        let topLevelWidgets =
            currentDashboard.getIn(["data", "widgets"]) ||
            currentDashboard.getIn(["data", "content", "widgets"]);
        //filter static widgets that already exist in the store
        topLevelWidgets = topLevelWidgets.filter((widgetDescription) => {
            return !(
                getState().getIn(["config", widgetDescription.get("id"), "static"]) &&
                getState().getIn(["data", widgetDescription.get("id")])
            );
        });
        if (!topLevelWidgets) {
            return Promise.reject("Missing current dashboard data.");
        }
        return promiseAllSettled(
            topLevelWidgets.toJS().map((widgetDescription) => {
                //extracting "waitInnerWidgetsLoading" and "skipLoadingAnimation" from widget config for the current dashboard
                let widgetTemplate = getCurrentDashboardWidgetConfig(
                    widgetDescription.id,
                    currentDashboard
                );
                //using widget properties with priority
                let widgetWaitInnerWidgetsLoading = widgetTemplate.waitInnerWidgetsLoading;
                if (widgetWaitInnerWidgetsLoading === undefined) {
                    widgetWaitInnerWidgetsLoading = waitInnerWidgetsLoading;
                }
                let widgetSkipLoadingAnimation = widgetTemplate.skipLoadingAnimation;
                if (widgetSkipLoadingAnimation === undefined) {
                    widgetSkipLoadingAnimation = skipLoadingAnimation;
                }
                return dispatch(
                    loadWidget(
                        widgetDescription,
                        widgetSkipLoadingAnimation,
                        {},
                        widgetWaitInnerWidgetsLoading
                    )
                );
            })
        );
    };
}

function getCurrentDashboard(state) {
    if (!state) {
        const dispatch = webuiState.getDispatchMethod();
        state = dispatch((dispatch, getState) => getState());
    }
    let currentPath = state.getIn(["routes", "current"]);
    return state.get("dashboards").find((dashboard) => dashboard.get("address") === currentPath);
}

export function getCurrentDashboardId() {
    let dashboard = getCurrentDashboard();
    if (dashboard) {
        return dashboard.id;
    }
}

function getCurrentDashboardWidgetConfig(id, currentDashboardImmutable = false) {
    if (!currentDashboardImmutable) {
        currentDashboardImmutable = getCurrentDashboard();
        if (!currentDashboardImmutable) {
            return {};
        }
    }
    const widgetTemplates = currentDashboardImmutable.getIn(["config", "widgets"]);
    if (!widgetTemplates) {
        return {};
    }
    const widgetTemplateImmutable = widgetTemplates.find((item) => item.get("id") === id) || {};
    return widgetTemplateImmutable.toJS ? widgetTemplateImmutable.toJS() : {};
}

//creates dashboard id from sirenAction "href"
//expects href to follow the format: /widget/:dashboardId
//another options is for the dashboardId to be specified under properties.dashboardId
function getDashboardId(sirenAction = {}) {
    let id = "";
    let { targets = [], href = "" } = sirenAction;
    let targetContainer = targets && targets[0];
    if (href && targetContainer === "{root}") {
        id = href.split("/")[2] || id;
    }
    if (sirenAction.properties?.dashboardId) {
        id = sirenAction.properties.dashboardId;
    }
    return id;
}

function getDashboardDescription(sirenAction = {}) {
    let newAddress = sirenAction.href;
    let dashboardId = getDashboardId(sirenAction);
    return {
        id: dashboardId,
        //address: newAddress + dashboardId,
        address: newAddress,
        dataUrl: addQueryParams(newAddress, sirenAction.parameters),
        configUrl: addQueryParams(`${newAddress}/configuration`, sirenAction.parameters),
        properties: sirenAction.properties || {},
        parameters: sirenAction.parameters || {}
    };
}
//construct dashboard address as displayed in browser address bar
async function getDashboardAddress(sirenAction) {
    if (webuiState.hasAction("application.buildPageAddress")) {
        let { pathname, search } = await webuiState.getActionByName("application.buildPageAddress")(
            { sirenAction }
        );
        return { pathname, search };
    }
    let dashboardId = getDashboardId(sirenAction);
    let pathname = "";
    let search = "";
    let dashboardQueryName = webuiState.getGlobalProperties("dashboardQueryName") || "context";
    switch (webuiState.getGlobalProperties("dashboardPathname")) {
        case "queryParams":
            pathname = "/";
            search = `?${dashboardQueryName}=${dashboardId}`;
            break;
        case "pathParams":
            pathname = `/${dashboardId}`;
            break;
        default:
            pathname = "";
            search = "";
    }

    return {
        pathname,
        search
    };
}

function loadDashboardFailure(err) {
    return (dispatch) => {
        let message = translationService.translate("loadDashboardFailure");
        if (err.name === "ApiError") {
            let messages = {
                type: "error",
                message: err.message || message
            };
            return dispatch(webuiState.getActionByName("messages.triggerErrorMessage")(messages));
        }
    };
}

function getLocalStorageDashboard(location, state) {
    let storage = {};
    let dashboardPathname = webuiState.getGlobalProperties("dashboardPathname");
    if (dashboardPathname !== "queryParams") {
        return storage;
    }
    if (!state.getIn(["global", "applicationBoot"])) {
        return storage;
    }
    if (window.location.search === "") {
        return storage;
    }
    let dashboardQueryName = webuiState.getGlobalProperties("dashboardQueryName") || "context";
    let windowLocationSearch = window.location.search.includes(`?${dashboardQueryName}=`);
    if (dashboardPathname === "queryParams" && !windowLocationSearch) {
        return storage;
    }
    if (!locationChanged(location)) {
        return storage;
    }
    let retrievedState = JSON.parse(sessionStorage.getItem("state")) || {};
    let currentLocationState = retrievedState.dashboard || {};
    if (currentLocationState.hasOwnProperty("id")) {
        let pathname = "";
        let search = "";

        switch (dashboardPathname) {
            case "queryParams":
                pathname = "/";
                search = `?${dashboardQueryName}=${currentLocationState.id}`;
                break;
            case "pathParams":
                pathname = `/${currentLocationState.id}`;
                break;
            default:
                pathname = "";
                search = "";
        }

        storage.pathname = pathname;
        storage.search = search;

        storage.dashboard = {
            id: currentLocationState.id,
            properties: currentLocationState.properties,
            parameters: currentLocationState.parameters,
            dataUrl: currentLocationState.dataUrl,
            configUrl: currentLocationState.configUrl
        };
    }

    return storage;
}

function getWidgetProperty(state, widgetId, propName) {
    let propertyValue;
    let widgets = state.get("data");
    let widgetState = widgets.find((widgetData) => {
        let result;
        let widgetClass = widgetData.get("class");
        if (widgetClass) {
            result = widgetClass.includes(widgetId);
        }

        if (!result) {
            result = widgetData.get("widget") === widgetId;
        }
        return result;
    });
    if (widgetState) {
        propertyValue = widgetState.get(propName);
    }
    return propertyValue;
}

function getInnerWidgets(widgetID, state, innerWidgets = []) {
    let widgets = state.get("data");
    widgets.forEach((widgetData) => {
        let parentWidgetId = widgetData.get("parentWidget");
        let parentWidget = parentWidgetId && parentWidgetId === widgetID;
        if (parentWidget) {
            let widget = widgetData.get("widget");
            innerWidgets.push(widget);
            getInnerWidgets(widget, state, innerWidgets);
        }
    });
    return innerWidgets;
}
